import {
  useIsFeedbackOnFeedbackV2Enabled,
  useUpdateSummaryFlag,
} from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import ReportOffRoundedIcon from '@mui/icons-material/ReportOffRounded';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import { SummaryFlag } from 'generated/graphql';
import capitalize from 'lodash/capitalize';
import { MouseEvent, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface FlagSummaryButtonProps {
  feedbackID: string;
  initialFlag?: SummaryFlag;
  summaryFlagType: SummaryFlag;
}

export const FlagSummaryButton = ({
  feedbackID,
  initialFlag,
  summaryFlagType,
}: FlagSummaryButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { isFeedbackOnFeedbackV2Enabled } = useIsFeedbackOnFeedbackV2Enabled();
  const flaggedSummaryRecords = useFeedbackOnFeedbackStore(
    ({ flaggedSummaryRecords }) => flaggedSummaryRecords,
  );

  const flaggedRecord = flaggedSummaryRecords.find(
    (record) => record.id === feedbackID,
  );
  const isFlagged = flaggedRecord
    ? flaggedRecord.summaryFlagType === summaryFlagType
    : initialFlag === summaryFlagType;

  const { updateSummaryFlag, isLoading } = useUpdateSummaryFlag({
    feedbackID,
    summaryFlagType,
    initialFlag,
  });

  const handleMouseOver = () => {
    setIsHovered(true);
  };

  const handleMouseOut = () => {
    setIsHovered(false);
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    updateSummaryFlag({
      variables: {
        request: {
          userID,
          recordID: feedbackID,
          tenantID: tenantID,
          ...(isFlagged
            ? { removeFlag: summaryFlagType }
            : { addFlag: summaryFlagType }),
        },
      },
    });
  };

  const getStartIcon = () => {
    if (!isFlagged) {
      return <OutlinedFlagIcon />;
    }

    if (isHovered) {
      return <ReportOffRoundedIcon />;
    }

    return <FlagRoundedIcon />;
  };

  const getButtonLabel = () => {
    if (!isFlagged && !isHovered) {
      return '';
    }

    if (isFlagged && !isHovered) {
      return `Flagged as "${capitalize(summaryFlagType)} Summary”`;
    }

    if (isFlagged && isHovered) {
      return 'Remove Flag';
    }

    return `Flag Summary as ${summaryFlagType.toLowerCase()}`;
  };

  if (!isFeedbackOnFeedbackV2Enabled) {
    return null;
  }

  return (
    <LoadingButton
      loading={isLoading}
      disableRipple
      variant="text"
      size="small"
      sx={{
        color: isFlagged
          ? isHovered
            ? 'primary.main'
            : 'warning.main'
          : 'text.secondary',
        minWidth: isFlagged ? '300px' : 'auto',
        transition: '0.3s ease-in-out',
      }}
      startIcon={getStartIcon()}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onClick={handleClick}
    >
      <Box
        style={{
          opacity: isHovered || isFlagged ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
      >
        {getButtonLabel()}
      </Box>
    </LoadingButton>
  );
};
